import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { API_BASE_URL } from '../../config/api';
import { trackEvent } from '../../utils/gtag';
import { trackOpenAiTrialStarted } from '../../utils/oaiq';

interface GoogleButtonAltProps {
  text: string;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /** Surcharge l'endpoint API appelé après l'auth Google (ex: '/api/affiliate/register-google') */
  apiEndpoint?: string;
  /** Redirection forcée après succès */
  redirectTo?: string;
}

export function GoogleButtonAlt({ text, className, onSuccess, onError, apiEndpoint, redirectTo }: GoogleButtonAltProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleGoogleLogin = async () => {
    if (loading) return;
    
    // console.log supprimé
    setLoading(true);
    
    try {
      // ÉTAPE 1: Auth Google
      // console.log supprimé
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      // console.log supprimé
      
      // ÉTAPE 2: Appel API Backend
      // console.log supprimé
      const idToken = await user.getIdToken();
      // console.log supprimé
      
      const apiUrl = `${API_BASE_URL}${apiEndpoint ?? '/api/register-google'}`;
      
      const requestData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified
      };
      // console.log supprimé
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include', // cookies fp_attr / _ga → attribution Google Ads côté serveur
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestData)
      });
      
      // console.log supprimé
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error supprimé
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }
      
      const apiData = await response.json();
      // console.log supprimé
      
      // ÉTAPE 3: Redirection
      // console.log supprimé
      
      // Tracker le signup Google (uniquement pour les nouveaux utilisateurs)
      if (apiData.isNewUser) {
        trackEvent('signup_completed', { method: 'google' });
        trackOpenAiTrialStarted(apiData.uid || user.uid);
      }

      // Attendre un peu pour que tout se stabilise
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else if (redirectTo) {
          window.location.href = redirectTo;
        } else {
          // Redirection selon le statut d'onboarding
          if (apiData.isNewUser || apiData.onboardingCompleted === false) {
            window.location.href = '/onboarding';
          } else {
            window.location.href = '/dashboard';
          }
        }
      }, 1000);
      
    } catch (error: any) {
      // console.error supprimé
      // console.error supprimé
      // console.error supprimé
      
      // Gestion spécifique des erreurs
      let errorMessage = t('public.googleButtonAlt.default_error');

      if (error.message?.includes('Cross-Origin-Opener-Policy')) {
        // console.log supprimé
        setTimeout(async () => {
          const auth = getAuth();
          if (auth.currentUser) {
            // console.log supprimé
            window.location.href = '/dashboard';
          } else {
            // console.log supprimé
            if (onError) onError(new Error(t('public.googleButtonAlt.cors_error')));
            setLoading(false);
          }
        }, 2000);
        return;
      }

      if (error.message?.includes('API Error')) {
        errorMessage = `${t('public.googleButtonAlt.api_error_prefix')}${error.message}`;
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = t('public.googleButtonAlt.popup_blocked');
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = t('public.googleButtonAlt.popup_closed');
      } else {
        errorMessage = `${t('public.googleButtonAlt.generic_error_prefix')}${error.message}`;
      }
      
      // console.log supprimé
      
      if (onError) {
        onError(new Error(errorMessage));
      }
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogleLogin}
      disabled={loading}
      className={`flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${className || 'bg-[#0a0a0d] hover:bg-[#0a0a0d] text-white border border-brand-primary/30 transition-colors'}`}
    >
      {loading ? (
        <svg className="animate-spin h-5 w-5" style={{ color: 'inherit' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
        </svg>
      )}
      <span className="font-medium" style={{ color: 'inherit' }}>{loading ? t('public.login.connecting') : text}</span>
    </button>
  );
}
